import type { Expedition, ThreatProjection, WorldLocation } from "../types";

export const SURFACE_BOUNDS = {
  minX: 12,
  maxX: 88,
  minY: 19,
  maxY: 80,
} as const;

export const MIN_LOCATION_SEPARATION = 17;

const STARTING_CLOCK_SECONDS = 18 * 60 * 60 + 47 * 60;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

const knownArtKinds = [
  "pharmacy",
  "petrol_station",
  "warehouse",
  "farm",
  "police_station",
  "housing_estate",
  "supermarket",
  "clinic",
  "waterworks",
  "rail_yard",
  "broadcast_tower",
  "hospital",
  "research_campus",
  "military_depot",
  "refuge_camp",
] as const;

export type SurfaceArtKind = (typeof knownArtKinds)[number] | "unknown";
export type ConfidenceBand = "rumour" | "uncertain" | "observed" | "confirmed";
export type ReportAgeBand = "fresh" | "aging" | "stale";
export type DangerBand = "unknown" | "low" | "elevated" | "severe";
export type DepletionBand = "intact" | "disturbed" | "stripped" | "exhausted";
export type SurfaceDayPhase = "dawn" | "day" | "dusk" | "night";
export type SurfaceWeather = "rain" | "mist" | "overcast" | "wind" | "drizzle";

export interface SurfacePoint {
  x: number;
  y: number;
}

export interface SurfaceDecoration extends SurfacePoint {
  rotation: number;
  scale: number;
  variant: number;
}

export interface SurfaceLocationVisual extends SurfacePoint {
  id: string;
  artKind: SurfaceArtKind;
  artUrl: string;
  artVariant: 1 | 2 | 3;
  artFocusX: number;
  artFocusY: number;
  confidenceBand: ConfidenceBand;
  ageBand: ReportAgeBand;
  dangerBand: DangerBand;
  depletionBand: DepletionBand;
  reportAgeSeconds: number;
  routeBend: number;
}

export interface SurfaceThreatVisual {
  edge: "north" | "east" | "south" | "west";
  proximity: "distant" | "near" | "imminent" | "contact";
  density: "scattered" | "massing" | "swarm" | "unknown";
  confidenceBand: ConfidenceBand;
  kind: "wandering" | "migrating" | "pursuing" | "reported";
}

export interface SurfaceVisualProjection {
  bunker: SurfacePoint;
  locations: Record<string, SurfaceLocationVisual>;
  forests: SurfaceDecoration[];
  settlements: SurfaceDecoration[];
  sector: number;
  terrainVariant: number;
  riverVariant: number;
  reliefVariant: number;
  dayPhase: SurfaceDayPhase;
  weather: SurfaceWeather;
  threat: SurfaceThreatVisual | null;
}

interface SurfaceVisualInput {
  visualSeed: number;
  gameTime: number;
  locations: readonly WorldLocation[];
  threat: ThreatProjection | null;
  activeCrisis?: boolean;
}

function hash(seed: number, scope: string): number {
  let value = (2166136261 ^ (seed >>> 0)) >>> 0;
  for (let index = 0; index < scope.length; index += 1) {
    value ^= scope.charCodeAt(index);
    value = Math.imul(value, 16777619) >>> 0;
  }
  value ^= value >>> 16;
  value = Math.imul(value, 0x7feb352d) >>> 0;
  value ^= value >>> 15;
  value = Math.imul(value, 0x846ca68b) >>> 0;
  return (value ^ (value >>> 16)) >>> 0;
}

function randomSource(seed: number, scope: string): () => number {
  let value = hash(seed, scope);
  return () => {
    value = (value + 0x6d2b79f5) >>> 0;
    let mixed = value;
    mixed = Math.imul(mixed ^ (mixed >>> 15), mixed | 1);
    mixed ^= mixed + Math.imul(mixed ^ (mixed >>> 7), mixed | 61);
    return ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296;
  };
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function roundPoint(value: number): number {
  return Math.round(value * 100) / 100;
}

export function surfacePointDistance(left: SurfacePoint, right: SurfacePoint): number {
  return Math.hypot(left.x - right.x, left.y - right.y);
}

export function publicLocationArtKind(
  location: Pick<WorldLocation, "kind">,
): SurfaceArtKind {
  return (knownArtKinds as readonly string[]).includes(location.kind)
    ? (location.kind as SurfaceArtKind)
    : "unknown";
}

export function locationArtUrl(kind: SurfaceArtKind): string {
  return `/static/assets/world/locations/${kind}-v2.webp`;
}

export function confidenceVisualBand(confidence: number): ConfidenceBand {
  if (confidence < 40) return "rumour";
  if (confidence < 70) return "uncertain";
  if (confidence < 90) return "observed";
  return "confirmed";
}

export function reportAgeVisualBand(ageSeconds: number): ReportAgeBand {
  if (ageSeconds < 5 * 60) return "fresh";
  if (ageSeconds < 20 * 60) return "aging";
  return "stale";
}

export function dangerVisualBand(
  range: [number, number] | undefined,
): DangerBand {
  if (!range) return "unknown";
  if (range[1] <= 2) return "low";
  if (range[1] <= 4) return "elevated";
  return "severe";
}

export function depletionVisualBand(depletion: number): DepletionBand {
  if (depletion <= 0) return "intact";
  if (depletion < 48) return "disturbed";
  if (depletion < 90) return "stripped";
  return "exhausted";
}

function isValidLocationPoint(
  point: SurfacePoint,
  placed: readonly SurfacePoint[],
  bunker: SurfacePoint,
): boolean {
  if (
    point.x < SURFACE_BOUNDS.minX ||
    point.x > SURFACE_BOUNDS.maxX ||
    point.y < SURFACE_BOUNDS.minY ||
    point.y > SURFACE_BOUNDS.maxY
  ) {
    return false;
  }
  // Keep the report title legible in the north-west corner.
  if (point.x < 46 && point.y < 29) return false;
  if (surfacePointDistance(point, bunker) < 16) return false;
  return placed.every(
    (other) => surfacePointDistance(point, other) >= MIN_LOCATION_SEPARATION,
  );
}

function fallbackLocationPoint(
  seed: number,
  locationId: string,
  placed: readonly SurfacePoint[],
  bunker: SurfacePoint,
): SurfacePoint {
  const candidates: SurfacePoint[] = [];
  for (const y of [22, 38, 58, 76]) {
    for (const x of [15, 32, 68, 85]) candidates.push({ x, y });
  }
  candidates.sort(
    (left, right) =>
      hash(seed, `${locationId}:${left.x}:${left.y}`) -
      hash(seed, `${locationId}:${right.x}:${right.y}`),
  );
  return (
    candidates.find((point) => isValidLocationPoint(point, placed, bunker)) ?? {
      x: SURFACE_BOUNDS.minX,
      y: SURFACE_BOUNDS.maxY,
    }
  );
}

function placeLocations(
  seed: number,
  locations: readonly WorldLocation[],
  bunker: SurfacePoint,
): Record<string, SurfaceLocationVisual> {
  const ordered = [...locations].sort(
    (left, right) => left.distance - right.distance || left.id.localeCompare(right.id),
  );
  const distances = ordered.map((location) => location.distance);
  const minimumDistance = distances.length ? Math.min(...distances) : 0;
  const maximumDistance = distances.length ? Math.max(...distances) : 1;
  const distanceSpan = Math.max(1, maximumDistance - minimumDistance);
  const baseAngle = randomSource(seed, "location-angle")() * Math.PI * 2;
  const placed: SurfacePoint[] = [];
  const result: Record<string, SurfaceLocationVisual> = {};

  ordered.forEach((location, index) => {
    const random = randomSource(seed, `location:${location.id}`);
    const distanceFactor = (location.distance - minimumDistance) / distanceSpan;
    const authoredBand = location.range_band;
    const bandRadius = authoredBand === "far"
      ? { x: 39, y: 29 }
      : authoredBand === "regional"
        ? { x: 31, y: 23 }
        : { x: 23, y: 17 };
    const radiusX = authoredBand ? bandRadius.x : 23 + distanceFactor * 17;
    const radiusY = authoredBand ? bandRadius.y : 17 + distanceFactor * 12;
    const stableSlot = location.slot_index ?? index;
    const bandOffset = authoredBand === "far" ? .37 : authoredBand === "regional" ? .19 : 0;
    const stableAngle = baseAngle + stableSlot * GOLDEN_ANGLE + bandOffset;
    let point: SurfacePoint | null = authoredBand
      ? {
          x: roundPoint(clamp(bunker.x + Math.cos(stableAngle) * radiusX, SURFACE_BOUNDS.minX, SURFACE_BOUNDS.maxX)),
          y: roundPoint(clamp(bunker.y + Math.sin(stableAngle) * radiusY, SURFACE_BOUNDS.minY, SURFACE_BOUNDS.maxY)),
        }
      : null;

    for (let attempt = 0; attempt < 28 && !point; attempt += 1) {
      const angle = stableAngle + attempt * 0.59 + (random() - 0.5) * 0.22;
      const candidate = {
        x: roundPoint(bunker.x + Math.cos(angle) * radiusX + (authoredBand ? 0 : (random() - 0.5) * 3)),
        y: roundPoint(bunker.y + Math.sin(angle) * radiusY + (authoredBand ? 0 : (random() - 0.5) * 2.5)),
      };
      if (isValidLocationPoint(candidate, placed, bunker)) point = candidate;
    }

    point ??= fallbackLocationPoint(seed, location.id, placed, bunker);
    placed.push(point);
    const artKind = publicLocationArtKind(location);
    const variantScope = location.variant_id ?? "legacy";
    const artVariant = ((hash(seed, `${location.id}:${variantScope}:art-variant`) % 3) + 1) as 1 | 2 | 3;
    const reportAgeSeconds = Math.max(0, location.observation.observed_at);
    result[location.id] = {
      id: location.id,
      ...point,
      artKind,
      artUrl: locationArtUrl(artKind),
      artVariant,
      artFocusX: 43 + hash(seed, `${location.id}:${variantScope}:focus-x`) % 15,
      artFocusY: 44 + hash(seed, `${location.id}:${variantScope}:focus-y`) % 15,
      confidenceBand: confidenceVisualBand(location.observation.confidence),
      ageBand: "fresh",
      dangerBand: dangerVisualBand(location.observation.danger_range),
      depletionBand: depletionVisualBand(location.depletion),
      reportAgeSeconds,
      routeBend: roundPoint((random() - 0.5) * 10),
    };
  });
  return result;
}

function updateReportAges(
  visuals: Record<string, SurfaceLocationVisual>,
  locations: readonly WorldLocation[],
  gameTime: number,
): void {
  for (const location of locations) {
    const visual = visuals[location.id];
    if (!visual) continue;
    const reportAgeSeconds = Math.max(0, gameTime - location.observation.observed_at);
    visual.reportAgeSeconds = reportAgeSeconds;
    visual.ageBand = reportAgeVisualBand(reportAgeSeconds);
  }
}

function createDecorations(
  seed: number,
  scope: string,
  count: number,
  locations: readonly SurfaceLocationVisual[],
  bunker: SurfacePoint,
): SurfaceDecoration[] {
  const random = randomSource(seed, scope);
  const decorations: SurfaceDecoration[] = [];
  for (let attempt = 0; attempt < count * 15 && decorations.length < count; attempt += 1) {
    const point = {
      x: roundPoint(8 + random() * 84),
      y: roundPoint(13 + random() * 75),
    };
    if (point.x < 47 && point.y < 28) continue;
    if (surfacePointDistance(point, bunker) < 9) continue;
    if (locations.some((location) => surfacePointDistance(point, location) < 8)) continue;
    decorations.push({
      ...point,
      rotation: Math.round(random() * 34 - 17),
      scale: Math.round((0.72 + random() * 0.7) * 100) / 100,
      variant: Math.floor(random() * 3) + 1,
    });
  }
  return decorations;
}

function surfaceDayPhase(gameTime: number): SurfaceDayPhase {
  const worldSeconds = (STARTING_CLOCK_SECONDS + gameTime) % (24 * 60 * 60);
  const hour = worldSeconds / 3600;
  if (hour >= 5 && hour < 8) return "dawn";
  if (hour >= 8 && hour < 17) return "day";
  if (hour >= 17 && hour < 21) return "dusk";
  return "night";
}

function surfaceWeather(seed: number, gameTime: number): SurfaceWeather {
  const weatherBlock = Math.floor(Math.max(0, gameTime) / (30 * 60));
  if (weatherBlock === 0) return "rain";
  const options: readonly SurfaceWeather[] = [
    "rain",
    "mist",
    "overcast",
    "wind",
    "drizzle",
    "rain",
  ];
  return options[hash(seed, `weather:${weatherBlock}`) % options.length] ?? "overcast";
}

function threatVisual(
  seed: number,
  threat: ThreatProjection | null,
  activeCrisis: boolean,
): SurfaceThreatVisual | null {
  if (!threat) return null;
  const edges = ["north", "east", "south", "west"] as const;
  const upperEta = threat.eta_range_seconds[1];
  const upperStrength = threat.observation.strength_range?.[1];
  const proximity = activeCrisis
    ? "contact"
    : upperEta <= 10 * 60
      ? "imminent"
      : upperEta <= 30 * 60
        ? "near"
        : "distant";
  const density =
    upperStrength == null
      ? "unknown"
      : upperStrength <= 70
        ? "scattered"
        : upperStrength <= 140
          ? "massing"
          : "swarm";
  const kind = threat.kind.includes("pursuing")
    ? "pursuing"
    : threat.kind.includes("migrating")
      ? "migrating"
      : threat.kind.includes("wandering")
        ? "wandering"
        : "reported";
  return {
    edge: edges[hash(seed, `threat:${threat.id}`) % edges.length] ?? "north",
    proximity,
    density,
    confidenceBand: confidenceVisualBand(threat.observation.confidence),
    kind,
  };
}

export function createSurfaceVisuals(input: SurfaceVisualInput): SurfaceVisualProjection {
  const seed = input.visualSeed >>> 0;
  const bunkerRandom = randomSource(seed, "bunker-position");
  const bunker = {
    x: roundPoint(49 + (bunkerRandom() - 0.5) * 4),
    y: roundPoint(48 + (bunkerRandom() - 0.5) * 3),
  };
  const locations = placeLocations(seed, input.locations, bunker);
  updateReportAges(locations, input.locations, input.gameTime);
  const locationVisuals = Object.values(locations);

  return {
    bunker,
    locations,
    forests: createDecorations(seed, "forest", 9, locationVisuals, bunker),
    settlements: createDecorations(seed, "settlement", 5, locationVisuals, bunker),
    sector: (hash(seed, "sector") % 9) + 1,
    terrainVariant: (hash(seed, "terrain") % 3) + 1,
    riverVariant: (hash(seed, "river") % 3) + 1,
    reliefVariant: (hash(seed, "relief") % 3) + 1,
    dayPhase: surfaceDayPhase(input.gameTime),
    weather: surfaceWeather(seed, input.gameTime),
    threat: threatVisual(seed, input.threat, input.activeCrisis ?? false),
  };
}

export function expeditionMapPosition(
  expedition: Expedition,
  location: WorldLocation,
  bunker: SurfacePoint,
  destination: SurfacePoint,
): SurfacePoint {
  const distance = Math.max(1, location.distance);
  const phaseSeconds = expedition.phase_seconds ?? 0;
  const progress =
    expedition.phase === "searching"
      ? 1
      : expedition.phase === "returning"
        ? clamp(phaseSeconds / distance, 0, 1)
        : expedition.phase === "outbound"
          ? clamp(1 - phaseSeconds / distance, 0, 1)
          : expedition.phase === "lost"
            ? 0.78
            : 0;
  return {
    x: roundPoint(bunker.x + (destination.x - bunker.x) * progress),
    y: roundPoint(bunker.y + (destination.y - bunker.y) * progress),
  };
}
